import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import PaymentSection from "./PaymentSection";
import GalleryPopup from "../../sharedComponents/GalleryPopup";
import CustomOrderInstructionCard from "../../ordersForBuyForMe/customOrderInstructionCard";
import CustomBuyForMeButton from "../customButtonBuyForMe";
import LoadingSpinner from "../../animation/LoadingSpinner";
import CopyLinkWithoutBackground from "../../iFrame/copyLinkWithoutBackground";
import {
  deleteProductItem,
  getProductItemById,
} from "../../../redux/reducer/productReducer";
import {
  deleteCart,
  fetchMyCarts,
  payForCart,
  submitItemsToPending,
  updateCart,
} from "../../../redux/reducer/cartReducer";
import useLanguageDirection from "../../../hooks/useLanguageDirection";
import EmptyCart from "./EmptyCart";
import ProductGrid from "./ProductGrid";
import EditProductModal from "./EditProductModal";
import EditProductModalBeforeAddCart from "./EditProductModalBeforeAddCart";

const ItemsBuyMeSection = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const direction = useLanguageDirection();
  // const timeRemaining = useTimer(3600);

  // State management
  const [selectedIds, setSelectedIds] = useState([]);
  const [hasPricedItem, setHasPricedItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showGalleryPopup, setShowGalleryPopup] = useState(null);

  // Redux selectors
  const { allProducts, productsLoading, productsError } = useSelector(
    (state) => state.products
  );
  const { carts, cartsLoading, cartsError } = useSelector(
    (state) => state.carts
  );

  // Memoized values
  const isCartSource = useMemo(() => {
    return Array.isArray(carts)
      ? carts.find((cart) => ["pending", "priced"].includes(cart.status))
      : null;
  }, [carts]);

  const displayProducts = useMemo(() => {
    return isCartSource
      ? isCartSource.products?.filter((item) =>
          ["pending", "priced"].includes(item.status)
        )
      : allProducts?.filter((item) => item.status === "pending");
  }, [isCartSource, allProducts]);

  // Effects
  useEffect(() => {
    loadInitialData();
  }, [dispatch]);

  useEffect(() => {
    const pricedItemExists = allProducts?.some(
      (item) => item.status === "Priced"
    );
    setHasPricedItem(pricedItemExists);
  }, [allProducts]);

  // Data loading functions
  const loadInitialData = () => {
    if(carts === 0){
      dispatch(getProductItemById())
        .unwrap()
        .then(() => dispatch(fetchMyCarts()))
        .catch((error) => {
          console.error("Error in getProductItemById:", error);
          alert("Failed to load products: " + (error.message || error));
        });
    }
  };

  const reloadData = () => {
    dispatch(getProductItemById())
      .unwrap()
      .then(() => dispatch(fetchMyCarts()));
  };

  // Product actions
  const handleDeleteProduct = (itemId) => {
    dispatch(deleteProductItem(itemId))
      .unwrap()
      .then(() => {
        dispatch(getProductItemById());
        dispatch(fetchMyCarts());
      })
      .catch((error) => alert(error || t("FailedDeleteItem")));
  };

  const handleEditProduct = (item) => {
    setEditingItem(item);
  };

  const handleShowGallery = (images) => {
    setShowGalleryPopup(images);
  };

  // Cart actions
  const handleSubmitItems = async () => {
    if (allProducts && allProducts.length > 0) {
      const selectedIds = allProducts
        .filter((item) => item.status === "pending")
        .map((item) => item._id.toString());

      if (selectedIds.length > 0) {
        try {
          const res = await dispatch(submitItemsToPending(selectedIds));
          if (res.meta.requestStatus === "fulfilled") {
            dispatch(getProductItemById());
            dispatch(fetchMyCarts());
          } else {
            alert("Submission failed. Please try again.");
          }
        } catch (error) {
          console.error("Submission error:", error);
          alert("Something went wrong. Please try again.");
        }
      } else {
        alert("Please select at least one item.");
      }
    } else {
      alert("No items to submit.");
    }
  };



const handlePayCart = async (cartId) => {
  try {
    const result = await dispatch(payForCart(cartId));
    if (result.meta.requestStatus === "fulfilled") {
      const paymentUrl = result.payload.paymentUrl;
      if (paymentUrl) {

        window.location.href = paymentUrl;
      } else {
        alert("No payment URL returned.");
      }
    } else {
      alert("Payment failed.");
    }
  } catch (err) {
    console.error("Error:", err);
  }
};



  const handleDeleteCart = (cartId) => {
    const confirmDelete = window.confirm(t("cart.confirmDelete")); 
    if (!confirmDelete) return; 

    try {
      dispatch(deleteCart(cartId))
        .unwrap()
        .then(() => {
          dispatch(fetchMyCarts());
        });
    } catch (err) {
      console.error("Error:", err);
      alert(t("cart.deleteError"));
    }
  };

  const handleUpdateCart = (cartId, data) => {
    dispatch(updateCart({ cartId, data }));
  };

  // Loading state
  if (productsLoading || cartsLoading) {
    return <LoadingSpinner />;
  }

  // Empty state
  if (allProducts?.length === 0 || productsError || cartsError) {
    return (
      <EmptyCart
        isCartSource={isCartSource}
        productsLength={allProducts?.length || 0}
      />
    );
  }
  // console.log(isCartSource);
  
  return (
    <div className="my-5 lg:my-0">
      <ProductGrid
        products={displayProducts}
        onDelete={handleDeleteProduct}
        onEdit={handleEditProduct}
        onShowGallery={handleShowGallery}
        selectedIds={selectedIds}
      />

      {/* Submit Button */}
      {!isCartSource &&
        allProducts?.some((item) => item.status === "pending") && (
          <CustomBuyForMeButton
            children={t("send_for_pricing")}
            icon={ArrowLeftIcon}
            onClick={handleSubmitItems}
          />
        )}

      {/* Payment Section */}
      {isCartSource && (
        <PaymentSection
          cart={isCartSource}
          onPayCart={handlePayCart}
          onDeleteCart={handleDeleteCart}
          onUpdateCart={handleUpdateCart}
          direction={direction}
          t={t}
        />
      )}

      {/* Instructions */}
      {(isCartSource || allProducts?.some((item) => item.status === "pending").length > 0) && (
        <div className="gap-5 mt-5 md:flex">
          <CustomOrderInstructionCard title={t("order_instruction_1")} />
          <CustomOrderInstructionCard title={t("order_instruction_2")} />
        </div>
      )}

      {/* Copy Link */}
      {(!isCartSource || allProducts.length === 0) && (
        <CopyLinkWithoutBackground />
      )}

      {/* Modals */}
      {editingItem && !editingItem.inCart &&  (
        <EditProductModalBeforeAddCart
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onUpdate={reloadData}
          direction={direction}
        />
      )}
      {editingItem && editingItem.inCart &&  (
        <EditProductModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onUpdate={reloadData}
          direction={direction}
        />
      )}



      {showGalleryPopup && (
        <GalleryPopup
          images={showGalleryPopup}
          onClose={() => setShowGalleryPopup(null)}
        />
      )}
    </div>
  );
};

export default ItemsBuyMeSection;
